// Read a payment screenshot and match it to one of the known QR names.
// The photos are phone screens, so the text is messy. We only accept a hit
// when a payee we already know shows up (Blue Pearl, Mohsin, and so on).

const RECEIPT_PROXY = "https://us-central1-bluepearl-e9750.cloudfunctions.net/receiptImage";

const RULES = [
    { test: /PEARL|PEARY|PEAR1|P3ARL/, label: /pearl/i },
    { test: /MOHS[I1L]?N|MOHSN/, label: /mohsin/i },
    { test: /RIZWAN/, label: /rizwan/i },
    { test: /YASEEN|YASIN|YASEAN/, label: /yaseen/i },
    { test: /IBRAN|1BRAN|IBRAM/, label: /ibran/i },
    { test: /BILQ[EU]E?S|BILQIS/, label: /bilq/i },
    { test: /RAUSHAN|RAUSHN|ROSHAN/, label: /raushan|roshan/i },
];

export function mediaIdOf(url) {
    const m = String(url || "").match(/booking-media\/([A-Za-z0-9_]+)/);
    return m ? m[1] : "";
}

export function guessQrName(text, qrNames) {
    const raw = String(text || "").toUpperCase();
    const names = Array.isArray(qrNames) ? qrNames : [];
    const at = raw.search(/TO:|TO |PAID TO|BANKING NAME/);
    const slices = at >= 0 ? [raw.slice(at, at + 90), raw] : [raw];
    for (const slice of slices) {
        for (const rule of RULES) {
            if (!rule.test.test(slice)) continue;
            const hit = names.find((n) => rule.label.test(n));
            if (hit) return hit;
        }
    }
    return "";
}

let workerPromise = null;

function worker() {
    if (!workerPromise) {
        workerPromise = import("tesseract.js").then(({ createWorker }) => createWorker("eng"));
    }
    return workerPromise;
}

function loadImage(blob) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("receipt image")); };
        img.src = url;
    });
}

// Phone photos come in sideways and on a dark screen. Straighten, stretch the
// contrast, and try the inverted colours before giving up.
function paintReceipt(img, angle, invert) {
    const swap = angle === 90 || angle === 270;
    const srcW = swap ? img.height : img.width;
    const srcH = swap ? img.width : img.height;
    const scale = Math.min(2.2, 1500 / Math.max(srcW, srcH, 1));
    const cw = Math.max(3, Math.round(srcW * scale));
    const ch = Math.max(3, Math.round(srcH * scale));
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, cw, ch);
    ctx.translate(cw / 2, ch / 2);
    ctx.rotate((angle * Math.PI) / 180);
    ctx.drawImage(img, -(img.width * scale) / 2, -(img.height * scale) / 2, img.width * scale, img.height * scale);
    const frame = ctx.getImageData(0, 0, cw, ch);
    const d = frame.data;
    let min = 255;
    let max = 0;
    for (let i = 0; i < d.length; i += 4) {
        const y = (0.3 * d[i]) + (0.59 * d[i + 1]) + (0.11 * d[i + 2]);
        d[i] = y;
        if (y < min) min = y;
        if (y > max) max = y;
    }
    const span = Math.max(1, max - min);
    for (let i = 0; i < d.length; i += 4) {
        let v = ((d[i] - min) * 255) / span;
        if (invert) v = 255 - v;
        d[i] = d[i + 1] = d[i + 2] = v;
    }
    ctx.putImageData(frame, 0, 0);
    return canvas;
}

/** @returns {Promise<string>} a known QR name, or "" when the photo cannot be read */
export async function readReceiptQr(imageUrl, qrNames) {
    const id = mediaIdOf(imageUrl);
    if (!id) return "";
    const res = await fetch(`${RECEIPT_PROXY}?id=${id}`);
    if (!res.ok) throw new Error(`receipt image HTTP ${res.status}`);
    const img = await loadImage(await res.blob());
    const w = await worker();
    // Upright first. Only turn the photo when that does not find a name.
    for (const angle of [0, 90, 270, 180]) {
        for (const invert of [false, true]) {
            const canvas = paintReceipt(img, angle, invert);
            // eslint-disable-next-line no-await-in-loop
            const { data } = await w.recognize(canvas);
            const name = guessQrName(data.text, qrNames);
            if (name) return name;
        }
    }
    return "";
}
